import { Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect } from "react";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { Driver, EldDailyRecord } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import PageHeader from "src/components/page-header";
import { addDaysToDate, formatDateForPreview, today } from "src/utils/date-utils";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import PageFilterLoader, { usePageFilterLoaderData } from "src/components/page-filter-loader";
import NoneFoundReport from "src/components/none-found-report";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useRouter } from "src/routes/hooks";
import { Violations } from "src/components/violations";


type EldLogsProps = {
  eldDailyRecords: EldDailyRecord[],
  onEldDailyRecordSelected: (a: EldDailyRecord) => void
}

const EldLogs = memo(({ eldDailyRecords, onEldDailyRecordSelected }: EldLogsProps) => {
  const { sortedList, sortData } = useSort(eldDailyRecords);

  if (!sortedList) {
    return null;
  }

  return <div>
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortData={sortData} sortKey="dailyDate">
                Date
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="eldDriverFirstName">
                Driver
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="totalHoursDriving" align="right">
                Hours Driven
              </SortableHeader>

              <TruxTableCell align="center">
                Violations
              </TruxTableCell>

              <SortableHeader sortData={sortData} sortKey="isCertified" align="right">
                Form & Manner
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((eldLog, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onEldDailyRecordSelected(eldLog)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell>
                {formatDateForPreview(eldLog.dailyDate)}
              </TruxTableCell>
              <TruxTableCell>
                {eldLog.eldDriverFirstName} {eldLog.eldDriverLastName}
              </TruxTableCell>
              <TruxTableCell align="right">
                {eldLog.totalHoursDriving.toFixed(2)}
              </TruxTableCell>
              <TruxTableCell align="center">
                <Violations violations={eldLog.violations} />
              </TruxTableCell>
              <TruxTableCell align="right">
                {eldLog.isCertified ? "Certified" : "Not Certified"}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  eldDailyRecords?: EldDailyRecord[],
  drivers?: Driver[]
}

export default function Page() {
  const settings = useSettingsContext();
  const pageFilterData = usePageFilterLoaderData(
    addDaysToDate(today(), -2),
    today()
  );
  const router = useRouter();

  const dataLoader = useCallback(() => ({
    eldDailyRecords: !pageFilterData.fromDate || !pageFilterData.fromDate
      ? []
      : axios.get(
        formatEndpoint(endpoints.eldLogs.findAll, {
          fromDate: pageFilterData.fromDate,
          toDate: // today() >= pageFilterData!.toDate! ? addDaysToDate(today(), - 1) :
            pageFilterData.toDate,
          eldDriverId: -1,
          violationType: 0,
        })
      ).then(response => response.data),
  }), [pageFilterData]);
  const {
    data,
    updateData,
    setData,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);


  const onEldDailyRecordSelected = useCallback((eldDailyRecord: EldDailyRecord) => {
    router.push(`/app/eld/eld-driver-logs/single/${eldDailyRecord.eldDriverId}/${eldDailyRecord.dailyDate}`)
  }, [router]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="All Logs" />

        <PageFilterLoader p={0} pageFilterData={pageFilterData} onRun={() => {
          setShouldRefresh(true);
        }} />

        {data && (data.eldDailyRecords?.length || 0) > 0 && <EldLogs eldDailyRecords={data.eldDailyRecords || []} onEldDailyRecordSelected={onEldDailyRecordSelected} />}
        {loaded && data && data.eldDailyRecords?.length === 0 && <NoneFoundReport />}
      </DisplayPageData>
    </Container >
  );
}
